// PRD 07 §3.2 — Add / Edit slide panel.
//
// Layout:
//   • Slices 1–6: single column at 520 px (form only).
//   • Slice 7+:   780 px with a 2-column grid — left = form + content block
//                 list, right = Patient Screen Preview.
//
// Save flows:
//   • Save Draft → DRAFT (toast only, no deployment guidance)
//   • Save       → PUBLISHED → toast + InfoModal (PRO Deployment guidance) +
//                  add GID to the deploy dirty list
// PRD §3.4 — a PUBLISHED guideline can't go back to DRAFT, so the Save Draft
// button is hidden when editing a row whose current status is PUBLISHED.
// PRD §8.2 — re-saving a PUBLISHED row prompts a confirm before the save.

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useWriteGuard } from '@/shared/hooks/useWriteGuard';
import { canAddBlock } from '@/shared/utils/block-validation';
import { useDeployDirtyStore } from '@/features/pro-deployment/store/deploy-dirty.store';
import { ConfirmModal } from '@/components/feedback/ConfirmModal';
import { InfoModal } from '@/components/feedback/InfoModal';
import { SlidePanel } from '@/components/feedback/SlidePanel';
import { toast } from '@/components/feedback/useToast';

import {
  useCreateGuideline,
  useUpdateGuideline,
} from '../hooks/use-guideline-mutations';
import {
  useGuidelineDetailQuery,
  useTopicsQuery,
} from '../hooks/use-guidelines';
import { useManagementGuidelinesPageStore } from '../store/management-guidelines-page.store';
import {
  ManagementGuidelineApiError,
  type ContentBlock,
  type ContentBlockType,
  type GuidelineGrade,
  type GuidelineStatus,
  type ManagementGuidelineDetail,
} from '../types/management-guidelines.types';
import { getGuidelineErrorMessage } from '../utils/error-message';
import {
  validateGuidelineForm,
  withoutEmptyMediaBlocks,
  type GuidelineFormDraft,
  type GuidelineFormErrors,
  type GuidelineFormFieldKey,
} from '../utils/validate-form';
import { CascadingTopicSelect } from './CascadingTopicSelect';
import { ContentBlockList } from './ContentBlockList';
import { GradeRadioGroup } from './GradeRadioGroup';
import { MobilePreview } from './MobilePreview';

interface FormState extends GuidelineFormDraft {
  blocks: ContentBlock[];
}

const EMPTY_FORM: FormState = {
  name: '',
  category: '',
  subArea: '',
  linkedItem: '',
  grade: null,
  blocks: [],
};

function formFromDetail(detail: ManagementGuidelineDetail | undefined): FormState {
  if (!detail) return EMPTY_FORM;
  return {
    name: detail.name,
    category: detail.category,
    subArea: detail.subArea,
    linkedItem: detail.linkedItem,
    grade: detail.grade,
    blocks: detail.blocks ?? [],
  };
}

// PRD §4.3 block id — `Date.now()` plus an in-instance sequence so rapid
// "Add" clicks don't collide (Date.now() resolution is 1ms).
let blockSeq = 0;
function nextBlockId(): number {
  blockSeq += 1;
  return Date.now() + blockSeq;
}

function buildBlock(type: ContentBlockType): ContentBlock {
  const base: ContentBlock = {
    id: nextBlockId(),
    type,
    title: '',
    text: '',
    showText: false,
  };
  if (type === 'video') base.url = '';
  return base;
}

const NAME_MAX = 200;

const FIELD_KEYS: readonly GuidelineFormFieldKey[] = [
  'name',
  'category',
  'subArea',
  'linkedItem',
  'grade',
];

export function GuidelineFormPanel() {
  const { t } = useTranslation('management-guidelines');
  const { canWrite, denyProps } = useWriteGuard('METADATA');

  const panel = useManagementGuidelinesPageStore((s) => s.panel);
  const closePanel = useManagementGuidelinesPageStore((s) => s.closePanel);

  const isOpen = panel !== null;
  const isEdit = panel?.mode === 'edit';
  const editingGid = panel?.mode === 'edit' ? panel.gid : null;

  const { data: topics = [] } = useTopicsQuery();
  const { data: detail, isLoading: isDetailLoading } = useGuidelineDetailQuery(editingGid);

  const createMutation = useCreateGuideline();
  const updateMutation = useUpdateGuideline();
  const isPending = createMutation.isPending || updateMutation.isPending;
  // SHCT-582 — PRD 21 §7.4 P-2. QC's re-test opened this panel with METADATA:READ and found every
  // field still typeable: only the footer was withheld, so the operator could rewrite a guideline
  // and only discover at the end that there was nothing to save it with. Locking the inputs is the
  // same rule the counseling-manual and health-encyclopedia panels already follow.
  const fieldsDisabled = !canWrite || (isEdit && isDetailLoading);

  const addDirtyGuideline = useDeployDirtyStore((s) => s.addGuideline);

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<GuidelineFormErrors>({});
  // Holds the user's intended status when the re-save confirm is showing so
  // performSave knows which path to run after they accept.
  const [pendingStatus, setPendingStatus] = useState<GuidelineStatus | null>(null);
  // Open after a successful PUBLISHED save — non-blocking, just acknowledges.
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  // SHCT-777 — the duplicate-combination refusal. A Business Logic Error must be acknowledged in a
  // modal, never flashed as a toast (error standard §7.1): the operator has to learn that a guideline
  // for this 영역·하위영역·항목·Grade already exists and that the fix is to edit THAT one, which a
  // toast that disappears on its own cannot convey. Holds the resolved message, or null when closed.
  const [duplicateMessage, setDuplicateMessage] = useState<string | null>(null);
  // Tracks whether the user has touched the form since it was last hydrated
  // or saved. Drives the discard-changes confirm on panel close.
  const [isDirty, setIsDirty] = useState(false);
  const [showDiscard, setShowDiscard] = useState(false);

  // PRD §3.4 — PUBLISHED → DRAFT is forbidden, so hide Save Draft for rows
  // whose current status is PUBLISHED.
  const currentStatus: GuidelineStatus | null = detail?.status ?? null;
  const hideSaveDraft = isEdit && currentStatus === 'PUBLISHED';

  // Repopulate whenever the panel switches target (create ↔ edit, or edit
  // detail finishes loading). Resets form + errors for create mode.
  useEffect(() => {
    if (!isOpen) return;
    setErrors({});
    setPendingStatus(null);
    if (isEdit) {
      // Wait for detail before hydrating; otherwise the form would flash empty
      // and then fill, causing the cascading selects to reset.
      if (detail) setForm(formFromDetail(detail));
    } else {
      setForm(EMPTY_FORM);
    }
  }, [isOpen, isEdit, detail]);

  // Reset the dirty + discard-modal state when the panel target changes.
  // Keyed on isOpen + isEdit + editingGid (not `detail`) so a background
  // detail refetch doesn't silently flip dirty back to false while the user
  // is editing.
  useEffect(() => {
    setIsDirty(false);
    setShowDiscard(false);
  }, [isOpen, isEdit, editingGid]);

  const title = isEdit
    ? t('form.titleEdit')
    : t('form.titleAdd');

  const clearFieldError = (key: GuidelineFormFieldKey) => {
    setErrors((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const updateName = (next: string) => {
    setForm((f) => ({ ...f, name: next.slice(0, NAME_MAX) }));
    setIsDirty(true);
    clearFieldError('name');
  };

  const updateCascade = (next: { category: string; subArea: string; item: string }) => {
    setForm((f) => ({
      ...f,
      category: next.category,
      subArea: next.subArea,
      linkedItem: next.item,
    }));
    setIsDirty(true);
    // Cascade changes can touch all three fields; clear any related errors.
    setErrors((prev) => {
      const out = { ...prev };
      delete out.category;
      delete out.subArea;
      delete out.linkedItem;
      return out;
    });
  };

  const updateGrade = (g: GuidelineGrade) => {
    setForm((f) => ({ ...f, grade: g }));
    setIsDirty(true);
    clearFieldError('grade');
  };

  // SHCT-370 — PRD 07 §3.2 line 126 caps a guideline at MAX_BLOCKS and requires the ADD itself to be
  // blocked, not merely the save. The guard sits on the state updater rather than only on the buttons so a
  // double-click, a keyboard repeat or any future caller cannot slip past a disabled control. The server
  // refuses an over-cap save too (TOO_MANY_BLOCKS), but by then the author has already written the block.
  const addBlock = (type: ContentBlockType) => {
    setForm((f) =>
      canAddBlock(f.blocks.length) ? { ...f, blocks: [...f.blocks, buildBlock(type)] } : f,
    );
    setIsDirty(true);
  };

  const updateBlock = (id: number, patch: Partial<ContentBlock>) => {
    setForm((f) => ({
      ...f,
      blocks: f.blocks.map((b) => (b.id === id ? { ...b, ...patch } : b)),
    }));
    setIsDirty(true);
  };

  const deleteBlock = (id: number) => {
    setForm((f) => ({ ...f, blocks: f.blocks.filter((b) => b.id !== id) }));
    setIsDirty(true);
  };

  // Map server-side field errors back onto the form. The mock embeds Korean
  // messages in `e.message`; we prefer the i18n-keyed copy via
  // getGuidelineErrorMessage when available.
  const applyApiError = (e: unknown) => {
    const localized = getGuidelineErrorMessage(
      e,
      t,
      'toasts.saveError',
    );
    // SHCT-777 — Business Logic Error → Modal (error standard §7.1). It returns before the toast:
    // showing both would make the same refusal arrive twice, and the toast is the half that vanishes.
    if (
      e instanceof ManagementGuidelineApiError &&
      e.code === 'GUIDELINE_DUPLICATE'
    ) {
      // Name the guideline already holding this combination when the server identifies it, so the
      // operator can go straight to it instead of hunting the list for a row they cannot see.
      const gid = e.meta?.gid;
      const ref =
        typeof gid === 'string' && gid
          ? t('errors.GUIDELINE_DUPLICATE_REF', {
              gid,
              name: typeof e.meta?.name === 'string' ? e.meta.name : '',
            })
          : '';
      setDuplicateMessage(`${localized}${ref}`);
      return;
    }
    if (e instanceof ManagementGuidelineApiError && e.fields) {
      const next: GuidelineFormErrors = {};
      for (const k of Object.keys(e.fields)) {
        if ((FIELD_KEYS as readonly string[]).includes(k)) {
          next[k as GuidelineFormFieldKey] = localized;
        }
      }
      setErrors((prev) => ({ ...prev, ...next }));
    }
    toast.error(localized);
  };

  const performSave = async (status: GuidelineStatus) => {
    if (isPending) return;
    try {
      const baseInput = {
        name: form.name.trim(),
        grade: form.grade as GuidelineGrade,
        category: form.category,
        subArea: form.subArea,
        linkedItem: form.linkedItem,
        // PRD §4.3 / §4.2 — content blocks ride along on every save. 0 blocks
        // is explicitly allowed. §3 [SHCT-662] — a media block the author never filled in is
        // excluded from the payload rather than blocking the save.
        blocks: withoutEmptyMediaBlocks(form.blocks),
        status,
      };

      let savedGid: string;
      if (isEdit && editingGid) {
        const updated = await updateMutation.mutateAsync({
          gid: editingGid,
          input: baseInput,
        });
        savedGid = updated.gid;
      } else {
        const created = await createMutation.mutateAsync(baseInput);
        savedGid = created.gid;
      }

      // Saved successfully — the form no longer has unsaved changes, so any
      // subsequent close path (InfoModal dismiss, manual close) should skip
      // the discard confirm.
      setIsDirty(false);

      if (status === 'PUBLISHED') {
        addDirtyGuideline(savedGid);
        toast.success(t('toasts.saved'));
        // PRD §3.4 — guidance modal is mandatory on PUBLISHED save (not on
        // draft). It stays up until the user acknowledges.
        setInfoModalOpen(true);
      } else {
        toast.success(t('toasts.savedDraft'));
        closePanel();
      }
    } catch (e) {
      applyApiError(e);
    }
  };

  const handleSaveClick = (status: GuidelineStatus) => {
    if (!canWrite || isPending) return;

    const result = validateGuidelineForm(form, t);
    setErrors(result.errors);
    if (!result.ok) return;

    // PRD §8.2 — re-saving a PUBLISHED guideline prompts a confirm because it
    // may affect linked PRO items. This only triggers on PUBLISHED → PUBLISHED;
    // first publish from DRAFT, draft saves, and creates go straight through.
    if (isEdit && currentStatus === 'PUBLISHED' && status === 'PUBLISHED') {
      setPendingStatus(status);
      return;
    }

    void performSave(status);
  };

  const handleResaveConfirm = () => {
    const target = pendingStatus;
    setPendingStatus(null);
    if (target) void performSave(target);
  };

  const handlePanelClose = () => {
    if (isPending) return;
    // If the user typed anything since the panel opened or the last save,
    // confirm before discarding. `setIsDirty(false)` after save makes the
    // successful-save path skip this prompt automatically.
    if (isDirty) {
      setShowDiscard(true);
      return;
    }
    closePanel();
  };

  const handleDiscardConfirm = () => {
    setShowDiscard(false);
    setIsDirty(false);
    closePanel();
  };

  return (
    <>
      <SlidePanel open={isOpen} title={title} width={780} onClose={handlePanelClose}>
        {/* 2-column layout on wide panels; collapses to a single column at
            narrow widths so the form + 320 px mobile preview never overflow
            on a phone-sized viewport (PRD §5 responsive). */}
        <div
          className="grid gap-6 grid-cols-[1fr_340px] max-[900px]:grid-cols-1"
        >
          {/* === Left column: form + content blocks === */}
          <div style={{ minWidth: 0 }}>
            <h3
              style={{
                fontSize: 16,
                fontWeight: 600,
                marginBottom: 16,
                color: 'var(--foreground)',
              }}
            >
              {t('form.sections.info')}
            </h3>

            {/* GID — readonly. Empty placeholder in create mode (BE assigns on save). */}
            <div className="form-field">
              <label className="form-field__label" htmlFor="mgGid">
                {t('form.fields.gid.label')}
              </label>
              <input
                id="mgGid"
                type="text"
                className="form-field__input"
                value={isEdit ? editingGid ?? '' : ''}
                placeholder={t('form.fields.gid.placeholder')}
                disabled
                style={{
                  background: 'var(--muted)',
                  color: 'var(--muted-foreground)',
                }}
              />
            </div>

            {/* Name — required, 200 char cap with live counter (PRD §2.2 #11, §4.2). */}
            <div className="form-field">
              <label className="form-field__label" htmlFor="mgName">
                {t('form.fields.name.label')}{' '}
                <span className="required">*</span>
              </label>
              <input
                id="mgName"
                type="text"
                className={`form-field__input${
                  errors.name ? ' form-field__input--error' : ''
                }`}
                value={form.name}
                onChange={(e) => updateName(e.target.value)}
                maxLength={NAME_MAX}
                placeholder={t('form.fields.name.placeholder')}
                disabled={fieldsDisabled}
                aria-invalid={errors.name ? 'true' : undefined}
              />
              <div
                style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}
              >
                <p className="form-field__error" style={{ minHeight: 16 }}>
                  {errors.name ?? ''}
                </p>
                <span
                  style={{
                    fontSize: 12,
                    color: 'var(--muted-foreground)',
                    marginTop: 4,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {form.name.length}/{NAME_MAX}
                </span>
              </div>
            </div>

            {/* Cascade — required (PRD §2.2 #12, §4.2). */}
            <div className="form-field">
              <label className="form-field__label">
                {t('form.fields.cascade.label')}{' '}
                <span className="required">*</span>
              </label>
              <CascadingTopicSelect
                topics={topics}
                category={form.category}
                subArea={form.subArea}
                item={form.linkedItem}
                onChange={updateCascade}
                disabled={fieldsDisabled}
              />
              {/* One inline error span covers all three selects — only the
                  first missing field is reported to keep the message concise. */}
              {errors.category || errors.subArea || errors.linkedItem ? (
                <p className="form-field__error">
                  {errors.category ?? errors.subArea ?? errors.linkedItem}
                </p>
              ) : null}
            </div>

            {/* SHCT-639 — the "PID (자동 연동)" field and its auto-derived chips are GONE.
                Requirement Clarification No.45 (2026-06-10, DONE) removed it: "현재 화면에서,
                [PID 자동매칭]은 삭제하겠습니다", and PRD 07 v1.7 replaced it with a 1:1 model —
                "관리지침 ↔ PRO 항목 1:1 매칭 … (이전 PID 자동 연동/자동 칩 표시 기능은 제거)".
                The link is now decided solely by 영역/하위영역/항목 + Grade, which the cascading
                select above and the Grade radio below already capture. Showing 5 chips for one
                guideline also contradicted that 1:1 model outright. */}

            {/* Grade — required (PRD §2.2 #14, §4.2). */}
            <div className="form-field">
              <label className="form-field__label">
                {t('form.fields.grade.label')}{' '}
                <span className="required">*</span>
              </label>
              <GradeRadioGroup
                value={form.grade}
                onChange={updateGrade}
                invalid={Boolean(errors.grade)}
                disabled={fieldsDisabled}
              />
              {errors.grade ? (
                <p className="form-field__error">{errors.grade}</p>
              ) : null}
            </div>

            {/* Section divider then Content Settings header — matches the
                prototype `<div class="section-divider"></div>` + h3 pattern. */}
            <div
              style={{
                height: 1,
                background: 'var(--border)',
                margin: '24px 0 16px',
              }}
            />
            <h3
              style={{
                fontSize: 16,
                fontWeight: 600,
                marginBottom: 16,
                color: 'var(--foreground)',
              }}
            >
              {t('form.sections.content')}
            </h3>

            <ContentBlockList
              blocks={form.blocks}
              error={errors.blocks}
              disabled={fieldsDisabled}
              onAddText={() => addBlock('text')}
              onAddImage={() => addBlock('image')}
              onAddVideo={() => addBlock('video')}
              onChangeBlock={updateBlock}
              onDeleteBlock={deleteBlock}
            />
          </div>

          {/* === Right column: Patient Screen Preview === */}
          <div>
            <h3
              style={{
                fontSize: 16,
                fontWeight: 600,
                marginBottom: 16,
                color: 'var(--foreground)',
              }}
            >
              {t('form.sections.preview')}
            </h3>
            <MobilePreview name={form.name} blocks={form.blocks} />
          </div>
        </div>

        {/* Footer — Save Draft (hidden for currently-PUBLISHED rows) + Save.
            Both gated by METADATA:WRITE and the in-flight mutation state.
            SHCT-582 — the pair used to be withheld entirely without WRITE; PRD 21 §7.4 P-2 keeps
            them on screen and `disabled` + 툴팁 instead. `hideSaveDraft` is a different rule (a
            PUBLISHED row has no draft state to return to) and stays a genuine hide.
            Lives outside the grid so the divider spans full panel width. */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            marginTop: 32,
            paddingTop: 16,
            borderTop: '1px solid var(--border)',
          }}
        >
          {hideSaveDraft ? null : (
            <button
              type="button"
              className="btn btn-default btn-secondary"
              onClick={() => handleSaveClick('DRAFT')}
              disabled={fieldsDisabled || isPending}
              {...denyProps}
            >
              {t('form.actions.saveDraft')}
            </button>
          )}
          <button
            type="button"
            className="btn btn-default btn-primary"
            onClick={() => handleSaveClick('PUBLISHED')}
            disabled={fieldsDisabled || isPending}
            {...denyProps}
          >
            {t('form.actions.save')}
          </button>
        </div>
      </SlidePanel>

      {/* PRD §8.2 — re-save confirm (PUBLISHED → PUBLISHED only). */}
      <ConfirmModal
        open={pendingStatus !== null}
        title={t('resaveConfirm.title')}
        message={t('resaveConfirm.message')}
        confirmLabel={t('resaveConfirm.confirm')}
        cancelLabel={t('resaveConfirm.cancel')}
        loading={isPending}
        onConfirm={handleResaveConfirm}
        onCancel={() => setPendingStatus(null)}
      />

      {/* PRD §3.4 — PRO Deployment guidance after PUBLISHED save. Closing the
          modal also closes the panel so the user lands back on the refreshed
          list. */}
      <InfoModal
        open={infoModalOpen}
        title={t('deployGuidance.title')}
        body={t('deployGuidance.message')}
        confirmLabel={t('deployGuidance.confirm')}
        onClose={() => {
          setInfoModalOpen(false);
          closePanel();
        }}
      />

      {/* SHCT-777 — the duplicate-combination refusal (PRD 07 §3 1:1 매칭). Acknowledged in a modal
          per error standard §7.1; the panel stays open with the form intact so the operator can
          change the 항목/Grade rather than lose what they typed. */}
      <InfoModal
        open={duplicateMessage !== null}
        title={t('duplicateModal.title')}
        body={duplicateMessage ?? ''}
        confirmLabel={t('duplicateModal.confirm')}
        onClose={() => setDuplicateMessage(null)}
      />

      {/* Discard-changes confirm — shown when the user closes the panel with
          unsaved edits. Same shape as disease-code's group form panel. */}
      <ConfirmModal
        open={showDiscard}
        title={t('discardConfirm.title')}
        message={t('discardConfirm.message')}
        confirmLabel={t('discardConfirm.confirm')}
        cancelLabel={t('discardConfirm.cancel')}
        confirmVariant="destructive"
        onConfirm={handleDiscardConfirm}
        onCancel={() => setShowDiscard(false)}
      />
    </>
  );
}
